import { spItemKeyFacet, spFolderItemSetKeyFacet, spListKeyFacet, spViewKeyFacet } from '../dataSource/Keys';
import { preferredViewFacet, listFacet, defaultViewFacet, documentSetFacet } from '../dataSource/Facets';
import { IControlHandler, createControlHandler } from '@ms/items-view/lib/private/addons/registerControlHandler';
import { openActionControl } from '@ms/items-view/lib/private/controls/openActionControl';
import { IItemCacheThunkAction, IItemCacheDispatch } from '@ms/items-view/lib/private/store/actions/ItemCacheActions';
import { IItemCache } from '@ms/items-view/lib/private/models/IItemCacheObserver';
import { getCurrentItemSetKey } from '@ms/items-view/lib/private/models/itemsScope/getCurrentItemSetKey';
import {
  createAsyncActionCreator,
  IDispatch,
  IAsyncActionCreator
} from '@ms/items-view/lib/private/store/actions/createActionCreator';
import { resolveItems } from '@ms/items-view/lib/private/operations/getItems';
import { parentItemFacet, schemaFacet } from '@ms/items-view/lib/private/facets/SetFacets';
import { IFaceted } from '@ms/items-view/lib/private/models/Facet';
import { IItemCacheState } from '@ms/items-view/lib/private/store/reducers/itemCacheReducer';
import { readItem } from '@ms/items-view/lib/private/operations/readItem';
import ListTemplateType from '@ms/odsp-datasources/lib/dataSources/listCollection/ListTemplateType';
import ListFilterUtilities from '@ms/odsp-datasources/lib/utilities/list/ListFilterUtilities';
import { Killswitch } from '@ms/odsp-utilities/lib/killswitch/Killswitch';
import { folderFacet } from '@ms/items-view/lib/private/facets/ItemFacets';
import { pack } from '@ms/items-view/lib/private/models/Facet';
import { DefaultPatch } from '@ms/items-view/lib/private/utilities/Graft';

export const openActionControlHandler: IControlHandler<typeof spItemKeyFacet.type, typeof openActionControl.type> =
  createControlHandler()(getOpenActionControl);

function getOpenActionControl(): IItemCacheThunkAction<typeof openActionControl.type> {
  return (dispatch: IItemCacheDispatch) => {
    return (itemCache: IItemCache, props: typeof openActionControl.inputType) => {
      const currentItemSetKey = getCurrentItemSetKey(itemCache);

      const overrideItemSetKey = getOverrideItemSetKeyForFolder(itemCache, {
        folderItemKey: props.itemKey,
        currentItemSetKey
      });

      const newSpFolderItemSet = itemCache.demandItemFacet(spFolderItemSetKeyFacet, props.itemKey);

      const itemSetForStaging = overrideItemSetKey || newSpFolderItemSet;

      const stageItem = itemSetForStaging && getStageItemOverride(itemCache, {
        itemSetKey: currentItemSetKey,
        parentItemKey: spItemKeyFacet.serialize(itemSetForStaging),
        stageItem: props.stageItem
      });

      if (!overrideItemSetKey) {
        const spItem = itemCache.demandItemFacet(spItemKeyFacet, props.itemKey);

        if (spItem && !spItem.itemId && itemCache.demandItemFacet(folderFacet, props.itemKey)) {
          return {
            itemKey: spFolderItemSetKeyFacet.serialize({
              webAbsoluteUrl: spItem.webAbsoluteUrl,
              listFullUrl: spItem.listFullUrl,
              rootFolder: spItem.rootFolder || ''
            })
          };
        }
      }

      return {
        ...(overrideItemSetKey ? { itemKey: spFolderItemSetKeyFacet.serialize(overrideItemSetKey) } : {}),
        ...(stageItem ? { stageItem } : {})
      };
    };
  };
}

/**
 * Return a ThunkAction to open new item set and also resolve the itemSet with parentItemKey key.
 * @param parentItemKey - the parentItemKey of the itemSet that about to be opened.
 * @param openItem - origin openItem thunkAction from openAction.
 */
export function getStageItemOverride(itemCache: IItemCache, props: {
  itemSetKey: string | undefined;
  parentItemKey: string | undefined;
  stageItem?: (typeof openActionControl.overridesType)['stageItem'];
}): (typeof openActionControl.overridesType)['stageItem'] | undefined {
  const {
    itemSetKey,
    parentItemKey,
    stageItem
  } = props;

  if (!parentItemKey) {
    return undefined;
  }

  return stageItem ? createAsyncActionCreator((payload: {
    itemsScopeItemKey: string;
    currentItemKey: string;
  }): IItemCacheThunkAction<Promise<void>> => {
    const {
      itemsScopeItemKey,
      currentItemKey
    } = payload;

    return async (dispatch: IItemCacheDispatch) => {
      const {
        schema
      } = dispatch(readItem({
        schema: schemaFacet
      }, itemSetKey));

      dispatch(resolveItems({
        items: {
          [currentItemKey]: {
            ...parentItemFacet.pack({
              itemKey: parentItemKey
            }),
            ...(schema ? pack({
              schema: schemaFacet
            }, {
                schema: new DefaultPatch(schema)
              }) : {})
          }
        }
      }));

      await dispatch(stageItem({
        currentItemKey: currentItemKey,
        itemsScopeItemKey: itemsScopeItemKey
      }));
    };
  }) : undefined;
}

/**
 * @internal
 */
export function getOverrideItemSetKeyForFolder(
  itemCache: IItemCache,
  props: {
    folderItemKey: string | undefined;
    currentItemSetKey?: string;
    newFilterParams?: string;
  }
): typeof spFolderItemSetKeyFacet.type | undefined {
  const {
    folderItemKey,
    currentItemSetKey,
    newFilterParams
  } = props;

  if (currentItemSetKey && folderItemKey) {
    const deserializedSetKey = itemCache.demandItemFacet(spFolderItemSetKeyFacet, currentItemSetKey);
    const deserializedItemKey = itemCache.demandItemFacet(spItemKeyFacet, folderItemKey);
    if (deserializedSetKey && deserializedItemKey) {
      const {
        webAbsoluteUrl,
        listFullUrl,
        rootFolder,
        itemId
      } = deserializedItemKey;

      if (!rootFolder && itemId) {
        return undefined;
      }

      let filterParams = newFilterParams !== undefined ? (newFilterParams ? newFilterParams : undefined) : deserializedSetKey.filterParams;
      if (filterParams) {
        // We need to clear out LinkTitle filter params to avoid double filtering causing no result
        // since rootFolder params contains the same filter condition.
        // Example: using filter by title in List to find folder and then click into the folder.
        filterParams = ListFilterUtilities.addFilterInfoInQueryString(filterParams, {
          filterField: 'LinkTitle',
          filterValue: '',
          clearField: true
        });
      }

      const targetRootFolder: string = rootFolder || '';
      const spListItemKey = spListKeyFacet.serialize(deserializedItemKey);
      const list = spListItemKey && itemCache.demandItemFacet(listFacet, spListItemKey);
      const isODB = list && list.templateType === ListTemplateType.mySiteDocumentLibrary;

      const preferredFolderView = itemCache.demandItemFacet(preferredViewFacet, folderItemKey);
      const preferredFolderViewId = preferredFolderView && preferredFolderView.viewKey && preferredFolderView.viewKey.viewId;

      const docSetReset = !Killswitch.isActivated('DCB04EDF-E92C-40EF-8A0D-589AAC9B0927', '3/19/2019', 'Reset view leaving a DocSet');

      // We want to switch to a different view in two cases:
      // 1. When navigating into a folder which has a preferred view (e.g. a DocumentSet), or
      // 2. When navigating out of a DocumentSet (e.g. to a parent or sibling folder, but not to a subfolder)
      let targetViewId: string = preferredFolderViewId || '';
      const currentItemSetItemKey = docSetReset ? spItemKeyFacet.serialize(deserializedSetKey) : undefined;

      // ODB is currently opting out of DocumentSet-specific view changing behaviors
      const inDocumentSet = !!currentItemSetItemKey && !isODB && !!itemCache.demandItemFacet(documentSetFacet, currentItemSetItemKey);
      if (inDocumentSet && !preferredFolderViewId) {
        const navigatingDown = targetRootFolder.indexOf(deserializedSetKey.rootFolder) === 0;
        const targetView = !navigatingDown ? itemCache.demandItemFacet(defaultViewFacet, spListItemKey) : undefined;
        const targetViewKey = targetView && targetView.viewKey && itemCache.demandItemFacet(spViewKeyFacet, targetView.viewKey);
        if (targetViewKey) {
          targetViewId = targetViewKey.viewId || '';
        }
      }

      // If we are switching to the preferred view of a folder, reset the filter, sort, group-by, etc.
      // Basically, we want to act as if the user navigated directly into the folder in the new view.
      // If we are navigating out of a Document Set, we also want to reset to the default view of the target folder.
      const currentViewId = deserializedSetKey.viewKey && deserializedSetKey.viewKey.viewId;
      const overrideItemSetKey: typeof spFolderItemSetKeyFacet.type =
        targetViewId && (targetViewId !== currentViewId) ?
          {
            webAbsoluteUrl: webAbsoluteUrl,
            listFullUrl: listFullUrl,
            rootFolder: targetRootFolder,
            viewKey: !isODB ? {
              viewId: targetViewId
            } : undefined
          } : {
            ...deserializedSetKey,
            filterParams: filterParams,
            webAbsoluteUrl,
            listFullUrl,
            rootFolder: targetRootFolder,
            ...(isODB ? {
              viewKey: undefined
            } : {})
          };

      return overrideItemSetKey;
    }
  }

  return undefined;
}

// @ts-ignore
type __unused_declarations = Pick<
  IDispatch<never> |
  IAsyncActionCreator<never, never> |
  IFaceted |
  IItemCacheState,
  never>;
